import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { App as CapacitorApp } from "@capacitor/app";
import { Send, Settings2, FileText, MessageCircle, Gift, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import MobileLayout from "@/components/MobileLayout";
import { useAuth } from "@/contexts/AuthContext";

export default function ClientHome() {
  const { user } = useAuth();
  const [promos, setPromos] = useState([]);
  const [raffles, setRaffles] = useState([]);
  const [settings, setSettings] = useState({});
  const [pendingPayments, setPendingPayments] = useState([]);
  const [claiming, setClaiming] = useState(false);

  const loadHomeData = async () => {
    try {
      const settingsResp = await api.get("/settings");
      setSettings(settingsResp.data || {});
    } catch {
      setSettings({});
    }

    if (!user) {
      setPromos([]);
      setRaffles([]);
      setPendingPayments([]);
      setPendingPayments([]);
      return;
    }

    try {
      const [promosResp, rafflesResp, ordersResp] = await Promise.all([
        api.get("/promotions/mine"),
        api.get("/raffles/mine"),
        api.get("/orders/mine"),
      ]);
      setPromos(promosResp.data || []);
      setRaffles((rafflesResp.data || []).filter((r) =>
        r.active && r.status !== "awarded" && !(r.winner_order_codes || []).length
      ));
      setPendingPayments((ordersResp.data || []).filter((o) =>
        o.payment_status === "pending" && o.payment_checkout_url
      ));
    } catch {
      setPromos([]);
      setRaffles([]);
    }
  };

  useEffect(() => {
    loadHomeData();

    const onVisible = () => {
      if (!document.hidden) loadHomeData();
    };
    document.addEventListener("visibilitychange", onVisible);

    let sub;
    CapacitorApp.addListener("appStateChange", ({ isActive }) => {
      if (isActive) loadHomeData();
    }).then((s) => { sub = s; }).catch(() => {});

    const timer = setInterval(loadHomeData, 120000);

    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(timer);
      if (sub?.remove) sub.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const claimApp10 = async () => {
    if (!user) return;
    setClaiming(true);
    try {
      await api.post("/promotions/claim", { code: "APP10" });
      await loadPromos();
    } finally {
      setClaiming(false);
    }
  };

  const claimPromo = async (code) => {
    if (!user) return;
    setClaiming(true);
    try {
      await api.post("/promotions/claim", { code });
      await loadHomeData();
    } finally {
      setClaiming(false);
    }
  };

  const activatePrizeNextOrder = async (code) => {
    if (!user) return;
    setClaiming(true);
    try {
      await api.post(`/promotions/${code}/use-next-order`);
      await loadHomeData();
    } finally {
      setClaiming(false);
    }
  };

  const deactivatePrizeNextOrder = async (code) => {
    if (!user) return;
    setClaiming(true);
    try {
      await api.post(`/promotions/${code}/dont-use-next-order`);
      await loadHomeData();
    } finally {
      setClaiming(false);
    }
  };

  const ordersBlocked = !!settings.calendar_notice?.block_orders;

  const currentLevel = user?.effective_client_level || user?.client_level || "Bronce";
  const lifetimeOrders = user?.lifetime_orders || 0;
  const lifetimePages = user?.lifetime_pages || 0;
  const levels = settings.loyalty_levels || [];

  const currentIdx = levels.findIndex((l) => (l.label || l.id) === currentLevel);
  const nextLevel = currentIdx >= 0 ? levels.slice(currentIdx + 1).find((l) => !l.manual_only && l.mode !== "MANUAL") : null;

  let nextText = "Sigue acumulando pedidos terminados para subir de nivel.";
  let progressPct = 0;

  if (nextLevel) {
    const needOrders = Math.max(0, (nextLevel.min_orders || 0) - lifetimeOrders);
    const needPages = Math.max(0, (nextLevel.min_pages || 0) - lifetimePages);
    const mode = (nextLevel.mode || "OR").toUpperCase();

    const orderPct = nextLevel.min_orders ? Math.min(100, Math.round((lifetimeOrders / nextLevel.min_orders) * 100)) : 100;
    const pagePct = nextLevel.min_pages ? Math.min(100, Math.round((lifetimePages / nextLevel.min_pages) * 100)) : 100;

    progressPct = mode === "AND" ? Math.min(orderPct, pagePct) : Math.max(orderPct, pagePct);

    if (mode === "AND") {
      nextText = `Para subir a ${nextLevel.label}, te faltan ${needOrders} pedido(s) y ${needPages} página(s).`;
    } else {
      nextText = `Para subir a ${nextLevel.label}, te faltan ${needOrders} pedido(s) o ${needPages} página(s).`;
    }
  } else if (currentLevel === "Histórico") {
    nextText = "Eres cliente Histórico KAPEI. Gracias por formar parte de nuestra historia.";
    progressPct = 100;
  } else {
    nextText = "Has alcanzado el nivel automático más alto disponible.";
    progressPct = 100;
  }

  const nextLevelName = nextLevel?.label || null;
  const missingText = nextLevel
    ? nextText
        .replace(`Para subir a ${nextLevel.label}, te faltan `, "")
        .replace("pedido(s)", "pedidos")
        .replace("página(s).", "páginas restantes")
        .replace("página(s)", "páginas")
        .replace(".", "")
    : nextText;

  return (
    <MobileLayout>
      <div className="mb-6">
        <h1 className="text-4xl font-black tracking-tight text-slate-900 sm:text-5xl">
          Enviar archivos a KAPEI
        </h1>
        <p className="mt-2 text-base text-slate-600">
          {user ? `Hola, ${user.name}.` : "Elige una opción."} Normalmente pagarás al recoger. Algunos trabajos pueden requerir una señal o pago online.
        </p>
      </div>

      {user && pendingPayments.length > 0 && (
        <div className="mb-5 rounded-3xl border-2 border-emerald-300 bg-emerald-50 p-5 shadow-sm">
          <div className="text-xs font-black uppercase tracking-widest text-emerald-700">
            💳 Pagos pendientes
          </div>
          <h2 className="mt-1 text-xl font-black text-emerald-950">
            {pendingPayments.length === 1
              ? "Tienes un pago pendiente"
              : `Tienes ${pendingPayments.length} pagos pendientes`}
          </h2>

          <div className="mt-4 flex max-h-[330px] flex-col gap-3 overflow-y-auto pr-1">
            {pendingPayments.map((o) => (
              <Link
                key={o.id}
                to={`/codes/${encodeURIComponent(o.code)}`}
                className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-emerald-200"
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="font-mono text-lg font-black text-[#4d7c0f]">
                      {o.code}
                    </div>
                    <div className="text-xs font-semibold text-emerald-800">
                      Pulsa para revisar el pedido y pagar de forma segura.
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-black text-emerald-950">
                      {Number(o.payment_amount_eur || 0).toFixed(2)} €
                    </div>
                    <div className="text-xs font-black uppercase text-emerald-700">
                      Pagar ahora
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {pendingPayments.length > 3 && (
            <div className="mt-3 text-xs font-bold text-emerald-800">
              Desliza dentro de esta caja para ver todos tus pagos pendientes.
            </div>
          )}
        </div>
      )}

      {settings.calendar_notice && (
        <div className={`mb-5 rounded-3xl border-2 p-5 shadow-sm ${
          settings.calendar_notice.block_orders
            ? "border-red-200 bg-red-50"
            : "border-amber-200 bg-amber-50"
        }`}>
          <div className="text-xs font-black uppercase tracking-widest text-slate-600">
            Aviso KAPEI
          </div>
          <h2 className="mt-1 text-xl font-black text-slate-900">
            {settings.calendar_notice.name || "Aviso importante"}
          </h2>
          {settings.calendar_notice.message && (
            <p className="mt-2 whitespace-pre-line text-sm font-semibold text-slate-700">
              {settings.calendar_notice.message}
            </p>
          )}
          {settings.calendar_notice.block_orders && (
            <div className="mt-3 rounded-xl bg-white/70 p-3 text-sm font-black text-red-700">
              Durante este periodo no se pueden enviar nuevos pedidos desde la app.
            </div>
          )}
        </div>
      )}

      {user && promos.length > 0 && (
        <div className="mb-5 flex flex-col gap-3">
          {promos.map((promo) => (
            <div key={promo.code} className="rounded-3xl border-2 border-[#d8b4e2] bg-gradient-to-br from-[#faf5ff] to-white p-5 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-[#4d7c0f] text-white">
                  {promo.claimed || promo.assigned ? <CheckCircle2 className="h-7 w-7" /> : <Gift className="h-7 w-7" />}
                </div>
                <div className="flex-1">
                  <div className="text-xs font-black uppercase tracking-widest text-[#7c3aed]">
                    Oferta KAPEI
                  </div>
                  <h2 className="mt-1 text-xl font-black text-slate-900">
                    {promo.title || promo.code}
                  </h2>
                  {promo.description && (
                    <p className="mt-1 text-sm text-slate-700">{promo.description}</p>
                  )}

                  {promo.assigned ? (
                    <div className="mt-3 rounded-xl bg-amber-100 p-3 text-sm font-bold text-amber-800">
                      Premio asignado al pedido{promo.assigned_order_code ? ` ${promo.assigned_order_code}` : ""}. KAPEI lo aplicará o entregará al prepararlo.
                    </div>
                  ) : promo.claimed ? (
                    promo.source === "raffle" ? (
                      <div className="mt-3 rounded-xl bg-amber-50 p-3 text-sm font-bold text-amber-900">
                        <div>Premio ganado pendiente.</div>
                        <div className="mt-1 text-xs font-semibold">
                          Actívalo solo cuando quieras usarlo. Debe canjearse completo en un único pedido; si el pedido es inferior, la parte no utilizada se perderá.
                        </div>
                        {promo.use_next_order ? (
                          <div className="mt-3">
                            <div className="rounded-xl bg-[#4d7c0f]/10 p-3 text-sm font-black text-[#4d7c0f]">
                              Preparado para el próximo pedido.
                            </div>
                            <button
                              type="button"
                              onClick={() => deactivatePrizeNextOrder(promo.code)}
                              disabled={claiming}
                              className="mt-2 rounded-xl border border-amber-300 bg-white px-4 py-2 text-xs font-black text-amber-900 disabled:opacity-60"
                            >
                              No usar ahora
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => activatePrizeNextOrder(promo.code)}
                            disabled={claiming}
                            className="mt-3 rounded-xl bg-[#4d7c0f] px-4 py-3 text-sm font-black text-white shadow hover:bg-[#3f6212] disabled:opacity-60"
                          >
                            Usar en mi próximo pedido
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="mt-3 rounded-xl bg-[#4d7c0f]/10 p-3 text-sm font-bold text-[#4d7c0f]">
                        Oferta activada. Se aplicará a tu próximo pedido.
                      </div>
                    )
                  ) : (
                    <button
                      type="button"
                      onClick={() => claimPromo(promo.code)}
                      disabled={claiming}
                      className="mt-3 rounded-xl bg-[#4d7c0f] px-4 py-3 text-sm font-black text-white shadow hover:bg-[#3f6212] disabled:opacity-60"
                    >
                      {claiming ? "Activando…" : "Activar oferta"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {user && raffles.length > 0 && (
        <div className="mb-5 rounded-2xl border-2 border-amber-200 bg-amber-50 p-4 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-amber-500 text-white">
              🏆
            </div>
            <div className="flex-1">
              <div className="text-sm font-black text-slate-900">
                {raffles.length === 1 ? "Hay un sorteo activo" : `Hay ${raffles.length} sorteos activos`}
              </div>
              <div className="mt-1 text-sm font-semibold text-slate-700">
                Haz un pedido y participa automáticamente. Consulta los detalles en Mi KAPEI.
              </div>
              <Link
                to="/club"
                className="mt-3 inline-flex rounded-full bg-[#4d7c0f] px-4 py-2 text-xs font-black text-white shadow hover:bg-[#3f6212]"
              >
                Ver detalles
              </Link>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-4">
        {ordersBlocked ? (
          <>
            <div
              data-testid="home-quick-send-disabled"
              className="kapei-btn-huge cursor-not-allowed bg-slate-300 text-slate-600 shadow-inner"
            >
              <div className="flex w-full items-center justify-between gap-4">
                <div className="flex items-start gap-4 text-left">
                  <Send className="mt-1 h-9 w-9 shrink-0" strokeWidth={3} />
                  <div>
                    <div className="text-2xl font-black leading-tight">Envío rápido</div>
                    <div className="text-sm font-bold opacity-90">Temporalmente no disponible</div>
                  </div>
                </div>
                <span className="rounded-full bg-white/40 px-3 py-1 text-xs font-bold uppercase tracking-wider">
                  Cerrado
                </span>
              </div>
            </div>

            <div
              data-testid="home-configure-send-disabled"
              className="kapei-btn-huge cursor-not-allowed border-4 border-slate-300 bg-slate-100 text-slate-500"
            >
              <div className="flex w-full items-start gap-4 text-left">
                <Settings2 className="mt-1 h-9 w-9 shrink-0" strokeWidth={3} />
                <div>
                  <div className="text-2xl font-black leading-tight">Configurar impresión</div>
                  <div className="text-sm font-bold">
                    Temporalmente no disponible por cierre de KAPEI
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <Link
              to="/send?mode=quick"
              data-testid="home-quick-send"
              className="kapei-btn-huge bg-[#4d7c0f] text-white shadow-lg hover:bg-[#3f6212]"
            >
              <div className="flex w-full items-center justify-between gap-4">
                <div className="flex items-start gap-4 text-left">
                  <Send className="mt-1 h-9 w-9 shrink-0" strokeWidth={3} />
                  <div>
                    <div className="text-2xl font-black leading-tight">Envío rápido</div>
                    <div className="text-sm font-medium opacity-90">Lo explico en tienda</div>
                  </div>
                </div>
                <span className="rounded-full bg-white/15 px-3 py-1 text-xs font-bold uppercase tracking-wider">
                  Recomendado
                </span>
              </div>
            </Link>

            <Link
              to="/send?mode=configured"
              data-testid="home-configure-send"
              className="kapei-btn-huge border-4 border-slate-900 bg-white text-slate-900 hover:bg-slate-50"
            >
              <div className="flex w-full items-start gap-4 text-left">
                <Settings2 className="mt-1 h-9 w-9 shrink-0" strokeWidth={3} />
                <div>
                  <div className="text-2xl font-black leading-tight">Configurar impresión</div>
                  <div className="text-sm font-medium text-slate-600">
                    Elegir copias, color, caras y más opciones
                  </div>
                </div>
              </div>
            </Link>
          </>
        )}
      </div>

      <div className="mt-10 grid grid-cols-2 gap-3">
        <Link to="/codes" data-testid="quick-myorders" className="kapei-card text-left">
          <FileText className="mb-2 h-7 w-7 text-[#4d7c0f]" strokeWidth={2.5} />
          <div className="font-extrabold text-slate-900">Mis códigos</div>
          <div className="text-xs text-slate-600">Ver pedidos y estado</div>
        </Link>
        <Link to={user ? "/quote" : "/login"} data-testid="quick-quote" className="kapei-card text-left">
          <MessageCircle className="mb-2 h-7 w-7 text-[#4d7c0f]" strokeWidth={2.5} />
          <div className="font-extrabold text-slate-900">Pedir presupuesto</div>
          <div className="text-xs text-slate-600">{user ? "O preguntar dudas" : "Requiere cuenta"}</div>
        </Link>
      </div>

      {user && (
        <div className="mt-8 rounded-2xl border-2 border-[#d8b4e2] bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div className="text-xs font-black uppercase tracking-widest text-[#7c3aed]">
              🎁 Club KAPEI
            </div>
            <div className="rounded-full bg-[#4d7c0f]/10 px-3 py-1 text-xs font-black text-[#4d7c0f]">
              Nivel {currentLevel}
            </div>
          </div>

          <div className="mt-3 h-2.5 overflow-hidden rounded-full bg-slate-200">
            <div
              className="h-full rounded-full bg-[#4d7c0f]"
              style={{ width: `${progressPct}%` }}
            />
          </div>

          <div className="mt-3 text-sm font-bold text-slate-900">
            {nextLevelName ? `Próximo nivel: ${nextLevelName}` : currentLevel === "Histórico" ? "Nivel Histórico" : "Nivel máximo alcanzado"}
          </div>
          <div className="mt-1 text-sm font-semibold text-slate-600">
            {missingText}
          </div>

          <div className="mt-3 flex justify-end">
            <Link
              to="/club"
              className="rounded-full bg-[#4d7c0f] px-4 py-2 text-xs font-black text-white shadow hover:bg-[#3f6212]"
            >
              Ver Mi KAPEI
            </Link>
          </div>
        </div>
      )}

      <div className="mt-8 rounded-2xl border-2 border-dashed border-[#d8b4e2] bg-[#faf5ff] p-5">
        <div className="text-sm font-bold uppercase tracking-widest text-[#7c3aed]">
          {user ? "Consejo KAPEI" : "Únete a Mi KAPEI"}
        </div>
        {user ? (
          <p className="mt-2 text-sm text-slate-700">
            También puedes enviarnos archivos desde el botón <strong>Compartir</strong> de tu móvil. Si ya tienes un pedido abierto, podremos relacionar mejor los nuevos archivos con tu trabajo.
          </p>
        ) : (
          <p className="mt-2 text-sm text-slate-700">
            Puedes enviar archivos sin registrarte. Pero si creas tu cuenta, tus pedidos contarán para subir de nivel, participar en sorteos y optar a regalos y ventajas del Club KAPEI.
          </p>
        )}
      </div>
    </MobileLayout>
  );
}
